import { BaseResponse } from './api';
import { GroupSummaryV1 } from './group';
import { MembershipPackageId } from './membership';

export interface AccountResponseV1 {
  accountId: string;
  userId: string;
  walletId?: string;
  name: string;
  description?: string;
  email?: string;
  isActive: boolean;
  createdOn: string;
  modifiedOn: string;
  archivedOn: string | null;
  groups: GroupSummaryV1[];
}

export interface AccountExistsResponseV1 {
  email: string;
  exists: boolean;
}

export interface MembershipFeatureV1 {
  featureId: string;
  featureCode: string;
  featureName: string;
  optionId: string;
  optionCode: string;
  optionName: string;
  isActive: boolean;
}

export interface MembershipDetailV1 {
  membershipId: string;
  groupId: string;
  userId?: string;
  packageId: string;
  packageName: string;
  packageCode?: string;
  status: MembershipStatus;
  assignedOn: string;
  /** AccessManagement sends `activatedOn`; `allocatedOn` is not a field on this contract. */
  activatedOn?: string;
  cancelledOn?: string;
  isActive: boolean;
  features: MembershipFeatureV1[];
}

export interface MembershipSummaryV1 {
  userId: string;
  email: string;
  name?: string;
  memberships: MembershipDetailV1[];
  activeMembershipCount: number;
}

export interface AccountMembershipResponseV1 extends BaseResponse {
  accountId: string;
  memberships: MembershipSummaryV1[];
  totalUsers: number;
  totalActiveMemberships: number;
}

export interface GroupMembershipResponseV1 {
  groupId: string;
  memberships: MembershipSummaryV1[];
  totalUsers: number;
  totalActiveMemberships: number;
}

export enum ProductLine {
  Marketplace = 'Marketplace',
  Health = 'Health'
}

export enum CatalogStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Deprecated = 'Deprecated'
}

export enum PricingType {
  Standalone = 'Standalone',
  Included = 'Included'
}

export enum PricingStructure {
  Unit = 'Unit',
  Aggregate = 'Aggregate'
}

export enum PricingStrategy {
  Static = 'Static',
  Rollup = 'Rollup'
}

export interface PackageResponseV1 extends BaseResponse {
  packageId: PACKAGE_ID;
  name: string;
  description?: string;
  packageCode?: string;
  categoryCode: string;
  shorthandCode: string;
  version: number;
  level: number;
  productLine: ProductLine;
  status: CatalogStatus;
  baselinePrice: number;
  oneTimeFee?: number;
  pricingType: PricingType;
  pricingStructure: PricingStructure;
  pricingStrategy: PricingStrategy;
  isDefault: boolean;
  isLatestVersion: boolean;
  isActive: boolean;
  createdOn: string;
  modifiedOn: string;
  optionIds: string[];
}

export interface OptionResponseV1 extends BaseResponse {
  optionId: string;
  name: string;
  description?: string;
  optionCode: string;
  categoryCode: string;
  shorthandCode: string;
  version: number;
  level: number;
  productLine: ProductLine;
  status: CatalogStatus;
  isRequired: boolean;
  baselinePrice: number;
  oneTimeFee?: number;
  pricingType: PricingType;
  pricingStructure: PricingStructure;
  pricingStrategy: PricingStrategy;
  isActive: boolean;
  isDefault: boolean;
  createdOn: string;
  modifiedOn: string;
  featureIds: string[];
}

export enum MembershipStatus {
  Pending = 'Pending',
  Active = 'Active',
  Cancelled = 'Cancelled',
  Suspended = 'Suspended'
}

export enum PricingLineItemType {
  Subscription = 'Subscription',
  OneTimeFee = 'OneTimeFee',
  Discount = 'Discount'
}

export interface MembershipCandidate extends CandidateMembershipV1 {
  displayName?: string;
  correlationId?: string;
  requiresInvitation?: boolean;
}

export interface CandidateMembershipV1 {
  invitationId?: string;
  userId?: string;
  inviteeEmail?: string;
  packageId: string;
}

export interface PricingPreviewWithCandidatesRequestV1 {
  candidatePackages: CandidateMembershipV1[];
}

export interface PricingLineItemV1 {
  membershipId: string | null;
  userId: string | null;
  inviteeEmail: string | null;
  packageId: string | null;
  label: string;
  amount: number;
  lineType: PricingLineItemType;
  ruleCode: string | null;
}

export interface MembershipPricingBreakdownV1 {
  membershipId: string | null;
  userId: string | null;
  inviteeEmail: string | null;
  packageId: string;
  packageName: string;
  paymentAccountId: string | null;
  status: MembershipStatus;
  isPrimary: boolean;
  subscriptionBaseline: number;
  subscriptionDiscount: number;
  subscriptionTotal: number;
  oneTimeFeeTotal: number;
}

export interface PricingPreviewResponseV1 {
  householdGroupId: string;
  householdMemberCount: number;
  memberships: MembershipPricingBreakdownV1[];
  lineItems: PricingLineItemV1[];
  subscriptionSubtotal: number;
  discountSubtotal: number;
  subscriptionTotal: number;
  oneTimeFeeTotal: number;
  grandTotalPreTax: number;
}

export interface FeatureResponseV1 {
  featureId: string;
  name: string;
  description?: string;
  featureCode: string;
  categoryCode: string;
  shorthandCode: string;
  version: number;
  level: number;
  productLine: ProductLine;
  status: CatalogStatus;
  featureType: string;
  baselinePrice: number;
  oneTimeFee?: number;
  inventory: number;
  pricingType: PricingType;
  pricingStructure: PricingStructure;
  pricingFrequency: string;
  usageFrequency: string;
  discountRate: number;
  discountType: string;
  isGroupLevel: boolean;
  isActive: boolean;
  isDefault: boolean;
  createdOn: string;
  modifiedOn: string;
  archivedOn?: string;
}

export interface PackageWithOptions extends PackageResponseV1 {
  options: OptionResponseV1[];
}

export interface PurchasePackageItemV1 {
  packageId: string;
  quantity: number;
}

export interface PurchasePackageForAccountRequestV1 {
  packages: PurchasePackageItemV1[];
}

export interface CreateAccountForm {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  zipCode: string;
  howFoundYoutopia: string;
  howFoundYoutopiaAdditional: string | null;
  acceptNumMeals: boolean;
  acceptTOS: boolean;
  acceptPrivacyPolicy: boolean;
  acceptConsentToTreat: boolean;
  acceptOver18: boolean;
}

export interface ChoosePlanForm {
  membershipPackageId: MembershipPackageId;
  numberOfPackages: number;
}

export interface ChangePlanForm {
  membershipPackageId: MembershipPackageId;
}

export enum ReferralSource {
  Friend = 'Friend',
  SocialMediaPost = 'SocialMediaPost',
  TVCommercial = 'TVCommercial',
  FunctionalMedicineDoctor = 'FunctionalMedicineDoctor',
  Nutritionist = 'Nutritionist',
  Dietician = 'Dietician',
  GymPromotion = 'GymPromotion',
  Other = 'Other'
}

export interface AboutYouForm {
  preferredName: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  referralSource: ReferralSource | null;
  referralSourceAdditional: string | null;
}

export interface InvitationOnboardingForm {
  preferredName: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  packageId: string | null;
  automatedOption: AutomatedOption | null;
  precisionOption: PrecisionOption | null;
  nourishmentOption: NourishmentOption | null;
}

export interface AddMemberForm {
  correlationId?: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  legalGuardian: boolean;
  hasSmartDevice: boolean;
  /**
   * An existing adult member of the household to make a second guardian of this dependent, or null
   * for the inviter alone. Offered only to a member who may manage guardianship, which the catalog
   * keeps with the owner.
   */
  guardianUserId: string | null;
  scopeIds: string[];
  packageId: string | null;
  automatedOption: AutomatedOption | null;
  precisionOption: PrecisionOption | null;
  nourishmentOption: NourishmentOption | null;
}

export interface PlanSelectionForm {
  packageId: string | null;
  automatedOption: AutomatedOption | null;
  precisionOption: PrecisionOption | null;
  nourishmentOption: NourishmentOption | null;
}

export enum PlanSelectionSection {
  Automated = 'Automated',
  Precision = 'Precision',
  Nourishment = 'Nourishment'
}
export const PlanSelectionSections = [
  PlanSelectionSection.Automated,
  PlanSelectionSection.Precision,
  PlanSelectionSection.Nourishment
];

export enum AutomatedOption {
  Automated = 'Automated'
}
export const AutomatedOptions = [AutomatedOption.Automated];

export enum PrecisionOption {
  Essential = 'Essential',
  Thrive = 'Thrive',
  Flourish = 'Flourish'
}
export const PrecisionOptions = [PrecisionOption.Essential, PrecisionOption.Thrive, PrecisionOption.Flourish];

export enum NourishmentOption {
  Minimal = 'Minimal',
  Boost = 'Boost',
  Accelerate = 'Accelerate'
}
export const NourishmentOptions = [NourishmentOption.Minimal, NourishmentOption.Boost, NourishmentOption.Accelerate];

// Payload returned when a plan detail sheet (Automated/Precision/Nourishment) is dismissed with a choice.
export interface PlanSelectionModalResponse {
  option: AutomatedOption | PrecisionOption | NourishmentOption;
}

export const OptionIdToOptionValue: Record<string, AutomatedOption | PrecisionOption | NourishmentOption> = {
  '20000000-0000-0000-0000-000000000001': AutomatedOption.Automated,
  '20000000-0000-0000-0000-000000000002': PrecisionOption.Essential,
  '20000000-0000-0000-0000-000000000003': PrecisionOption.Thrive,
  '20000000-0000-0000-0000-000000000004': PrecisionOption.Flourish,
  '20000000-0000-0000-0000-000000000005': NourishmentOption.Minimal,
  '20000000-0000-0000-0000-000000000006': NourishmentOption.Boost,
  '20000000-0000-0000-0000-000000000007': NourishmentOption.Accelerate
};

export const OptionIdToSection: Record<string, PlanSelectionSection> = {
  '20000000-0000-0000-0000-000000000001': PlanSelectionSection.Automated,
  '20000000-0000-0000-0000-000000000002': PlanSelectionSection.Precision,
  '20000000-0000-0000-0000-000000000003': PlanSelectionSection.Precision,
  '20000000-0000-0000-0000-000000000004': PlanSelectionSection.Precision,
  '20000000-0000-0000-0000-000000000005': PlanSelectionSection.Nourishment,
  '20000000-0000-0000-0000-000000000006': PlanSelectionSection.Nourishment,
  '20000000-0000-0000-0000-000000000007': PlanSelectionSection.Nourishment
};

export const PackageOptionsToPackageId = {
  [`${AutomatedOption.Automated}-${PrecisionOption.Essential}-${NourishmentOption.Minimal}`]:
    '30000000-0000-0000-0000-000000000001' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Essential}-${NourishmentOption.Boost}`]:
    '30000000-0000-0000-0000-000000000002' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Essential}-${NourishmentOption.Accelerate}`]:
    '30000000-0000-0000-0000-000000000003' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Thrive}-${NourishmentOption.Minimal}`]:
    '30000000-0000-0000-0000-000000000004' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Thrive}-${NourishmentOption.Boost}`]:
    '30000000-0000-0000-0000-000000000005' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Thrive}-${NourishmentOption.Accelerate}`]:
    '30000000-0000-0000-0000-000000000006' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Flourish}-${NourishmentOption.Minimal}`]:
    '30000000-0000-0000-0000-000000000007' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Flourish}-${NourishmentOption.Boost}`]:
    '30000000-0000-0000-0000-000000000008' as PACKAGE_ID,
  [`${AutomatedOption.Automated}-${PrecisionOption.Flourish}-${NourishmentOption.Accelerate}`]:
    '30000000-0000-0000-0000-000000000009' as PACKAGE_ID
};

export const PackageIdToPackageOptions = {
  ['30000000-0000-0000-0000-000000000001']: [
    AutomatedOption.Automated,
    PrecisionOption.Essential,
    NourishmentOption.Minimal
  ],
  ['30000000-0000-0000-0000-000000000002']: [
    AutomatedOption.Automated,
    PrecisionOption.Essential,
    NourishmentOption.Boost
  ],
  ['30000000-0000-0000-0000-000000000003']: [
    AutomatedOption.Automated,
    PrecisionOption.Essential,
    NourishmentOption.Accelerate
  ],
  ['30000000-0000-0000-0000-000000000004']: [
    AutomatedOption.Automated,
    PrecisionOption.Thrive,
    NourishmentOption.Minimal
  ],
  ['30000000-0000-0000-0000-000000000005']: [
    AutomatedOption.Automated,
    PrecisionOption.Thrive,
    NourishmentOption.Boost
  ],
  ['30000000-0000-0000-0000-000000000006']: [
    AutomatedOption.Automated,
    PrecisionOption.Thrive,
    NourishmentOption.Accelerate
  ],
  ['30000000-0000-0000-0000-000000000007']: [
    AutomatedOption.Automated,
    PrecisionOption.Flourish,
    NourishmentOption.Minimal
  ],
  ['30000000-0000-0000-0000-000000000008']: [
    AutomatedOption.Automated,
    PrecisionOption.Flourish,
    NourishmentOption.Boost
  ],
  ['30000000-0000-0000-0000-000000000009']: [
    AutomatedOption.Automated,
    PrecisionOption.Flourish,
    NourishmentOption.Accelerate
  ]
};

export type PACKAGE_ID =
  | '30000000-0000-0000-0000-000000000001'
  | '30000000-0000-0000-0000-000000000002'
  | '30000000-0000-0000-0000-000000000003'
  | '30000000-0000-0000-0000-000000000004'
  | '30000000-0000-0000-0000-000000000005'
  | '30000000-0000-0000-0000-000000000006'
  | '30000000-0000-0000-0000-000000000007'
  | '30000000-0000-0000-0000-000000000008'
  | '30000000-0000-0000-0000-000000000009';

export type PACKAGE_OPTION =
  | `${AutomatedOption.Automated}-${PrecisionOption.Essential}-${NourishmentOption.Minimal}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Essential}-${NourishmentOption.Boost}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Essential}-${NourishmentOption.Accelerate}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Thrive}-${NourishmentOption.Minimal}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Thrive}-${NourishmentOption.Boost}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Thrive}-${NourishmentOption.Accelerate}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Flourish}-${NourishmentOption.Minimal}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Flourish}-${NourishmentOption.Boost}`
  | `${AutomatedOption.Automated}-${PrecisionOption.Flourish}-${NourishmentOption.Accelerate}`;

export const DEFAULT_PACKAGE_ID: PACKAGE_ID =
  PackageOptionsToPackageId[`${AutomatedOption.Automated}-${PrecisionOption.Essential}-${NourishmentOption.Minimal}`];

// PaymentsEngine's membership order-discount rates for a household, resolved by the rules a checkout quote
// charges. Rates are percent points; memberRates is keyed by user id.
export interface HouseholdChannelRatesV1 {
  memberRates: Record<string, number>;
  guestRate: number;
}

export interface HouseholdOrderDiscountRatesV1 {
  groupId: string;
  scheduleAhead: HouseholdChannelRatesV1;
  onDemand: HouseholdChannelRatesV1;
}
